import { addMoney, type CurrencyCode, isZeroMoney, type Money, subtractMoney, zeroMoney } from '@taler/domain'
import { isRowComplete, rowCurrency, type RowDraft } from './draft.ts'
import type { Target } from './targets.ts'

/** What the lines account for so far, ignoring whether each line's target has resolved
 * yet - "remaining" has to update the moment an amount is typed, not only once the whole
 * split is complete. */
export function sumSplitAmounts(row: RowDraft, currency: CurrencyCode): Money {
  return row.splits.reduce(
    (sum, split) => (split.amount === null ? sum : addMoney(sum, split.amount)),
    zeroMoney(currency),
  )
}

/** The declared receipt total minus what the lines account for so far - positive while
 * lines still fall short, negative once they overshoot it, zero when the split is done. */
export function remainingToAllocate(
  row: RowDraft,
  total: Money | null,
  targetsById: ReadonlyMap<string, Target>,
): Money | undefined {
  const currency = rowCurrency(row, targetsById)
  if (total === null || currency === undefined) return undefined
  return subtractMoney(total, sumSplitAmounts(row, currency))
}

/** Ready to submit: every line resolves cleanly and the lines account for the whole
 * declared total, to the minor unit - the captain's "does not add up" gate. */
export function isSplitExpenseComplete(
  row: RowDraft,
  total: Money | null,
  targetsById: ReadonlyMap<string, Target>,
): boolean {
  if (!isRowComplete(row, targetsById)) return false
  const remaining = remainingToAllocate(row, total, targetsById)
  return remaining !== undefined && isZeroMoney(remaining)
}
